import {toast} from '@wandb/weave/common/components/elements/Toast';
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import copyToClipboard from 'copy-to-clipboard';
import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneLight} from 'react-syntax-highlighter/dist/esm/styles/prism';

import DocUrls from '../../../util/doc_urls';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
import {isOrgRegistryProjectName} from '../common/utils';
import {
  lowerSdkVersion,
  sdkVersionsTab,
  useSDKVersion,
} from './SDKVersionContext';

const linkArtifactCodeSnippet = (
  entityName: string,
  projectName: string,
  artifactTypeName: string,
  collectionName: string,
  sdkVersionValue: string
) => {
  let targetPath = `${projectName}/${collectionName}`;
  if (
    !isOrgRegistryProjectName(projectName) ||
    sdkVersionValue === lowerSdkVersion
  ) {
    targetPath = `${entityName}/${projectName}/${collectionName}`;
  }
  return `from pathlib import Path
import wandb

run = wandb.init(project="collection-linking-quickstart")

artifact_filepath = Path("./my_${artifactTypeName}_artifact.txt")
artifact_filepath.write_text("simulated ${artifactTypeName} file")

logged_artifact = run.log_artifact(
  artifact_filepath,
  "artifact-name",
  type="${artifactTypeName}"
)
run.link_artifact(
  artifact=logged_artifact,
  target_path="${targetPath}"
)
run.finish()`;
};

type ZeroVersionsProps = {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  collectionName: string;
};

export const ZeroVersions = ({
  entityName,
  projectName,
  artifactTypeName,
  collectionName,
}: ZeroVersionsProps) => {
  const isRegistryProject = isOrgRegistryProjectName(projectName);
  const {sdkVersionValue, setSdkVersionValue} = useSDKVersion();

  const onCopyLinkArtifactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(codeSnippet);
    toast('Copied to clipboard');
  };

  const codeSnippet = React.useMemo(
    () =>
      linkArtifactCodeSnippet(
        entityName,
        projectName,
        artifactTypeName,
        collectionName,
        sdkVersionValue
      ),
    [entityName, projectName, artifactTypeName, collectionName, sdkVersionValue]
  );

  return (
    <Tailwind style={{width: '100%', height: '100%'}}>
      <div className="flex h-full w-full flex-row gap-24">
        <div className="flex w-1/2 flex-col">
          <Banner className="items-start" variant="announcement">
            <BannerFlexWrapper>
              <div className="mr-auto">
                <Icon name="info" />
              </div>
              <BannerTextWrapper className="inline">
                <BannerBoldText>This collection is empty</BannerBoldText>
              </BannerTextWrapper>
            </BannerFlexWrapper>
          </Banner>
          <div className="p-16 text-base font-normal leading-[22.4px]">
            You can add your first version to this collection via the SDK in
            Python. Use the code shown to the right to (1) log an artifact to a
            run and (2) link it to this collection. To learn more ways to link a
            version, see{' '}
            <TargetBlank href={DocUrls.registry + 'link_version'}>
              <span className="cursor-pointer font-semibold text-teal-650 hover:text-teal-500">
                this guide
              </span>
            </TargetBlank>
            .
          </div>
        </div>
        <div className="h-full w-1/2">
          {isRegistryProject && (
            <div className="mb-16 flex">
              <Select
                options={[
                  sdkVersionsTab.lowerSdkVersion,
                  sdkVersionsTab.higherSdkVersion,
                ]}
                value={
                  sdkVersionValue === lowerSdkVersion
                    ? sdkVersionsTab.lowerSdkVersion
                    : sdkVersionsTab.higherSdkVersion
                }
                onChange={selected => {
                  if (selected != null && selected.value) {
                    setSdkVersionValue(selected.value);
                  }
                }}
                className="w-full"
              />
            </div>
          )}
          <div className="relative z-0 flex h-full w-full rounded bg-moon-50 p-8">
            <div className="absolute right-12 top-8 z-10">
              <Button
                icon="copy"
                variant="ghost"
                size="small"
                onClick={onCopyLinkArtifactClick}
                tooltip="Copy to clipboard">
                Copy
              </Button>
            </div>
            <div className="w-full flex-auto overflow-auto font-['Source_Code_Pro'] text-xs leading-4">
              <SyntaxHighlighter
                key={sdkVersionValue}
                language="python"
                style={oneLight}
                customStyle={{
                  backgroundColor: 'transparent',
                  overflow: 'auto !important',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: '0',
                }}>
                {codeSnippet}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </div>
    </Tailwind>
  );
};
